import { m } from 'framer-motion';
import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, orderBy, onSnapshot, updateDoc, doc } from 'firebase/firestore';
// @mui
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import Tabs from '@mui/material/Tabs';
import List from '@mui/material/List';
import Stack from '@mui/material/Stack';
import Badge from '@mui/material/Badge';
import Drawer from '@mui/material/Drawer';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
// hooks
import { useBoolean } from 'src/hooks/use-boolean';
import { useResponsive } from 'src/hooks/use-responsive';
// auth
import { useAuthContext } from 'src/auth/hooks';
// firebase
import { db } from 'src/utils/firebase';
// components
import Label from 'src/components/label';
import Iconify from 'src/components/iconify';
import Scrollbar from 'src/components/scrollbar';
import { varHover } from 'src/components/animate';
//
import NotificationItem from './notification-item';

// ----------------------------------------------------------------------

export default function NotificationsPopover() {
  const drawer = useBoolean();
  const smUp = useResponsive('up', 'sm');
  const navigate = useNavigate();
  const { user } = useAuthContext();

  const [currentTab, setCurrentTab] = useState('all');
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  // Subscribe to Firebase notifications in real-time
  useEffect(() => {

    if (!user?.id) {
      setLoading(false);
      return undefined;
    }

    const userId = user.id.toString();

    // Query notifications for this user
    const notificationsRef = collection(db, 'crm_notifications');

    const q = query(
      notificationsRef,
      where('userId', '==', userId),
      orderBy('createdAt', 'desc')
    );
    // Drop any docs created before this user existed. Guards against orphan
    // notifications whose userId collides with a recycled / re-used primary key.
    const userCreatedAtRaw =
      user.createdAt || user.created_date || user.created_at || null;
    const userCreatedAtMs = userCreatedAtRaw
      ? new Date(userCreatedAtRaw).getTime()
      : 0;

    // Real-time listener
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {

        const notificationsList = snapshot.docs
          .map((docSnapshot) => {
            const data = docSnapshot.data();
            return {
              id: docSnapshot.id,
              title: data.title || '',
              message: data.message || '',
              type: data.type || 'system',
              isUnRead: !data.read,
              createdAt: data.createdAt?.toDate() || new Date(),
              data: data.data || {},
              category: data.type === 'task_assigned' ? 'Task' :
                       data.type === 'subtask_assigned' ? 'Subtask' :
                       data.type === 'comment_mention' ? 'Mention' : 'System',
            };
          })
          .filter((n) =>
            !userCreatedAtMs || n.createdAt.getTime() >= userCreatedAtMs
          );

        setNotifications(notificationsList);
        setLoading(false);
      },
      (error) => {
        console.error('=== FIREBASE LISTENER ERROR ===');
        console.error('Error code:', error.code);
        console.error('Error message:', error.message);
        console.error('Full error:', error);
        setLoading(false);
      }
    );

    // console.log('onSnapshot listener set up successfully');

    // Cleanup subscription on unmount
    return () => {
      // console.log('Cleaning up notification listener');
      unsubscribe();
    };
  }, [user?.id]);

  const handleChangeTab = useCallback((event, newValue) => {
    setCurrentTab(newValue);
  }, []);

  const totalUnRead = notifications.filter((item) => item.isUnRead === true).length;

  const handleMarkAllAsRead = async () => {
    // Update local state
    setNotifications(
      notifications.map((notification) => ({
        ...notification,
        isUnRead: false,
      }))
    );

    // Update Firebase - mark all as read
    try {
      const updatePromises = notifications
        .filter((n) => n.isUnRead)
        .map((n) => updateDoc(doc(db, 'crm_notifications', n.id), { read: true }));

      await Promise.all(updatePromises);
      // console.log('All notifications marked as read');
    } catch (error) {
      console.error('Error marking notifications as read:', error);
    }
  };

  const handleMarkAsRead = async (notificationId) => {
    // Update local state
    setNotifications(
      notifications.map((notification) =>
        notification.id === notificationId
          ? { ...notification, isUnRead: false }
          : notification
      )
    );

    // Update Firebase
    try {
      await updateDoc(doc(db, 'crm_notifications', notificationId), { read: true });
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  // Filter notifications based on current tab
  const filteredNotifications = notifications.filter((notification) => {
    if (currentTab === 'unread') return notification.isUnRead;
    if (currentTab === 'archived') return !notification.isUnRead;
    return true; // 'all' tab
  });

  const TABS = [
    {
      value: 'all',
      label: 'All',
      count: notifications.length,
    },
    {
      value: 'unread',
      label: 'Unread',
      count: totalUnRead,
    },
    {
      value: 'archived',
      label: 'Read',
      count: notifications.length - totalUnRead,
    },
  ];

  const renderHead = (
    <Stack direction="row" alignItems="center" sx={{ py: 2, pl: 2.5, pr: 1, minHeight: 68 }}>
      <Typography variant="h6" sx={{ flexGrow: 1 }}>
        Notifications
      </Typography>

      {!!totalUnRead && (
        <Tooltip title="Mark all as read">
          <IconButton color="primary" onClick={handleMarkAllAsRead}>
            <Iconify icon="eva:done-all-fill" />
          </IconButton>
        </Tooltip>
      )}

      {!smUp && (
        <IconButton onClick={drawer.onFalse}>
          <Iconify icon="mingcute:close-line" />
        </IconButton>
      )}
    </Stack>
  );

  const renderTabs = (
    <Tabs value={currentTab} onChange={handleChangeTab}>
      {TABS.map((tab) => (
        <Tab
          key={tab.value}
          iconPosition="end"
          value={tab.value}
          label={tab.label}
          icon={
            <Label
              variant={((tab.value === 'all' || tab.value === currentTab) && 'filled') || 'soft'}
              color={
                (tab.value === 'unread' && 'info') ||
                (tab.value === 'archived' && 'success') ||
                'default'
              }
            >
              {tab.count}
            </Label>
          }
          sx={{
            '&:not(:last-of-type)': {
              mr: 3,
            },
          }}
        />
      ))}
    </Tabs>
  );

  const renderList = (
    <Scrollbar>
      {loading ? (
        <Stack alignItems="center" justifyContent="center" sx={{ py: 5 }}>
          <CircularProgress size={32} />
        </Stack>
      ) : filteredNotifications.length === 0 ? (
        <Stack alignItems="center" justifyContent="center" sx={{ py: 5 }}>
          <Iconify icon="solar:bell-off-bold-duotone" width={48} sx={{ color: 'text.disabled', mb: 1 }} />
          <Typography variant="body2" color="text.secondary">
            No notifications
          </Typography>
        </Stack>
      ) : (
        <List disablePadding>
          {filteredNotifications.map((notification) => (
            <NotificationItem
              key={notification.id}
              notification={notification}
              onMarkAsRead={() => handleMarkAsRead(notification.id)}
              onNavigate={(url) => { drawer.onFalse(); navigate(url); }}
            />
          ))}
        </List>
      )}
    </Scrollbar>
  );

  return (
    <>
      <IconButton
        component={m.button}
        whileTap="tap"
        whileHover="hover"
        variants={varHover(1.05)}
        color={drawer.value ? 'primary' : 'default'}
        onClick={drawer.onTrue}
      >
        <Badge badgeContent={totalUnRead} color="error">
          <Iconify icon="solar:bell-bing-bold-duotone" width={24} />
        </Badge>
      </IconButton>

      <Drawer
        open={drawer.value}
        onClose={drawer.onFalse}
        anchor="right"
        slotProps={{
          backdrop: { invisible: true },
        }}
        PaperProps={{
          sx: { width: 1, maxWidth: 420 },
        }}
      >
        {renderHead}

        <Divider />

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ pl: 2.5, pr: 1 }}
        >
          {renderTabs}
        </Stack>

        <Divider />

        {renderList}

        <Box sx={{ p: 1 }}>
          <Button fullWidth size="large" onClick={() => { drawer.onFalse(); navigate('/dashboard/notifications'); }}>
            View All
          </Button>
        </Box>
      </Drawer>
    </>
  );
}
